import { matchPath } from 'react-router-dom';
import DisableDevtool from 'disable-devtool';

const LISTING_DETAIL_ROUTE = '/l/:slug/:id';
const LOCALIZED_LISTING_DETAIL_ROUTE = '/:locale(th)/l/:slug/:id';
const LISTING_CANONICAL_ROUTE = '/l/:id';
const LISTING_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const SAFE_ROUTE = '/';
export const REQUIRED_DETECTION_COUNT = 2;
const EXPECTED_INACTIVE_REASONS = ['ignored route', 'seobot', 'server render', 'token passed'];
const DISABLE_DEVTOOL_BYPASS_MD5 = process.env.REACT_APP_DISABLE_DEVTOOL_BYPASS_MD5;

export const ALL_SUPPORTED_DETECTORS = [
  DisableDevtool.DetectorType.RegToString,
  DisableDevtool.DetectorType.DefineId,
  DisableDevtool.DetectorType.Size,
  DisableDevtool.DetectorType.DateToString,
  DisableDevtool.DetectorType.FuncToString,
  DisableDevtool.DetectorType.Debugger,
  DisableDevtool.DetectorType.Performance,
  DisableDevtool.DetectorType.DebugLib,
];

// Size multiplies inner dimensions by devicePixelRatio in 0.3.9, so it misreads normal zoom,
// sidebars, split-screen windows, and Retina displays. Keep Performance enabled as the fallback
// for desktop Chromium browsers when console-object and dock-geometry probes do not fire.
export const UNRELIABLE_DETECTORS = [DisableDevtool.DetectorType.Size];
export const ENABLED_DETECTORS = ALL_SUPPORTED_DETECTORS.filter(
  detector => !UNRELIABLE_DETECTORS.includes(detector)
);

let redirectInProgress = false;
let detectionCounts = new Map();
let replaceRoute = path => window.location.replace(path);

const resetDetectionState = () => {
  detectionCounts = new Map();
};

const redirectAwayFromProtectedListing = () => {
  if (
    typeof window === 'undefined' ||
    redirectInProgress ||
    !isListingDetailPath(window.location.pathname)
  ) {
    return;
  }

  redirectInProgress = true;
  DisableDevtool.isSuspend = true;

  try {
    replaceRoute(SAFE_ROUTE);
  } catch (error) {
    redirectInProgress = false;
    throw error;
  }
};

export const handleConfirmedInspectionAttempt = () => {
  redirectAwayFromProtectedListing();
};

export const handleDevtoolOpen = type => {
  if (
    typeof window === 'undefined' ||
    redirectInProgress ||
    !isListingDetailPath(window.location.pathname)
  ) {
    return;
  }

  const detectionCount = (detectionCounts.get(type) || 0) + 1;
  detectionCounts.set(type, detectionCount);

  if (detectionCount >= REQUIRED_DETECTION_COUNT) {
    redirectAwayFromProtectedListing();
  }
};

export const handleDevtoolClose = () => {
  resetDetectionState();
};

const matchesRoute = (pathname, path) =>
  matchPath(pathname || '', {
    path,
    exact: true,
    strict: false,
  });

export const isListingDetailPath = pathname => {
  const localizedOrPublicMatch =
    matchesRoute(pathname, LISTING_DETAIL_ROUTE) ||
    matchesRoute(pathname, LOCALIZED_LISTING_DETAIL_ROUTE);
  const canonicalMatch = matchesRoute(pathname, LISTING_CANONICAL_ROUTE);

  return !!(
    localizedOrPublicMatch ||
    (canonicalMatch && LISTING_ID_PATTERN.test(canonicalMatch.params.id))
  );
};

export const shouldIgnoreDisableDevtool = () =>
  typeof window === 'undefined' || !isListingDetailPath(window.location.pathname);

export const DISABLE_DEVTOOL_REWRITE_HTML = `
  <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;font-family:Arial,sans-serif;text-align:center;box-sizing:border-box;">
    <div>
      <h2>This page is unavailable.</h2>
      <p>Please close developer tools and reload the page.</p>
    </div>
  </div>
`;

export const disableDevtoolOptions = {
  md5: DISABLE_DEVTOOL_BYPASS_MD5 || undefined,
  url: '/',
  timeOutUrl: '/',
  tkName: 'ddtk',
  ondevtoolopen: handleDevtoolOpen,
  ondevtoolclose: handleDevtoolClose,
  // 0.3.9 accepts these aliases and normalizes them to the callbacks above.
  onDevtoolOpen: handleDevtoolOpen,
  onDevtoolClose: handleDevtoolClose,
  interval: 200,
  // The route-scoped hook owns context-menu blocking and can remove its listener on unmount.
  disableMenu: false,
  stopIntervalTime: 5000,
  clearIntervalWhenDevOpenTrigger: false,
  detectors: ENABLED_DETECTORS,
  clearLog: true,
  disableSelect: false,
  disableInputSelect: false,
  disableCopy: false,
  disableCut: false,
  disablePaste: false,
  ignore: shouldIgnoreDisableDevtool,
  disableIframeParents: false,
  // 0.3.9 adds this option; true prevents probes from running for recognized SEO bots.
  seo: true,
  rewriteHTML: DISABLE_DEVTOOL_REWRITE_HTML,
};

let lastInitializationResult = {
  success: false,
  reason: 'not initialized',
};

const statusWithRuntimeState = result => ({
  success: result.success,
  reason: result.reason,
  isRunning: DisableDevtool.isRunning,
  isSuspend: DisableDevtool.isSuspend,
  isRedirecting: redirectInProgress,
  version: DisableDevtool.version,
});

export const getListingDisableDevtoolStatus = () =>
  statusWithRuntimeState(lastInitializationResult);

export const activateListingDisableDevtool = onRedirect => {
  if (typeof window === 'undefined') {
    lastInitializationResult = { success: false, reason: 'server render' };
    return getListingDisableDevtoolStatus();
  }

  if (shouldIgnoreDisableDevtool()) {
    DisableDevtool.isSuspend = true;
    lastInitializationResult = { success: false, reason: 'ignored route' };
    return getListingDisableDevtoolStatus();
  }

  redirectInProgress = false;
  resetDetectionState();
  replaceRoute =
    typeof onRedirect === 'function' ? onRedirect : path => window.location.replace(path);

  if (DisableDevtool.isRunning) {
    lastInitializationResult = { success: true, reason: 'already running' };
  } else {
    lastInitializationResult = DisableDevtool(disableDevtoolOptions) || {
      success: false,
      reason: 'no initialization result',
    };
  }

  if (DisableDevtool.isRunning) {
    DisableDevtool.isSuspend = false;
  } else if (
    !lastInitializationResult.success &&
    !EXPECTED_INACTIVE_REASONS.includes(lastInitializationResult.reason)
  ) {
    // This contains only the package's non-sensitive initialization reason.
    console.warn(
      `[SceneAccess] Listing inspection deterrent was not initialized: ${lastInitializationResult.reason ||
        'unknown reason'}`
    );
  }

  return getListingDisableDevtoolStatus();
};

export const suspendListingDisableDevtool = () => {
  DisableDevtool.isSuspend = true;
  resetDetectionState();
  replaceRoute = path => window.location.replace(path);
  return getListingDisableDevtoolStatus();
};
